import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import MyPage from "../../MyPage";
import SubTabsWarehouses from "../../SubTabsWarehouses";
import { isSuperAdmin } from "../../helpers/user";
import axiosService from "../../helpers/axios";
import InventoriesTable from "../../components/InventoriesTable";
import { useSatellite } from "../../context/SatelliteContext";
import { appendSatelliteToSearchParams } from "../../helpers/satellite";

const MONTH_OPTIONS = [
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
];

export default function WarehousesInventoriesLeo() {
  const disabled = !isSuperAdmin();
  const { satellite } = useSatellite();

  const [fiscalYear, setFiscalYear] = useState("");
  const [month, setMonth] = useState("july");
  const [file, setFile] = useState(null);

  const [fiscalYears, setFiscalYears] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(true);

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadError, setUploadError] = useState(null);

  const [copying, setCopying] = useState(false);
  const [copyResult, setCopyResult] = useState(null);
  const [copyError, setCopyError] = useState(null);

  const [truncating, setTruncating] = useState(false);
  const [truncateResult, setTruncateResult] = useState(null);
  const [truncateError, setTruncateError] = useState(null);

  const [inventories, setInventories] = useState([]);
  const [loadingTable, setLoadingTable] = useState(true);
  const [tableRefreshKey, setTableRefreshKey] = useState(0);
  const fetchSeqRef = useRef(0);

  const EMPTY_FILTERS = {
    fiscalYear: "",
    warehouse: "",
    country: "",
    sapcode: "",
    product: "",
  };
  const [filterInputs, setFilterInputs] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);

  const monthOptions = useMemo(() => MONTH_OPTIONS, []);

  const setFilter = (key) => (e) =>
    setFilterInputs((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSearchInventories = () => setAppliedFilters({ ...filterInputs });

  const handleClearFilters = () => {
    setFilterInputs(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
  };

  const fetchInventoriesTmp = useCallback((filters = EMPTY_FILTERS, { bustCache = false } = {}) => {
    const seq = ++fetchSeqRef.current;
    setLoadingTable(true);
    const queryParams = new URLSearchParams();
    if (filters.fiscalYear) queryParams.append("year", filters.fiscalYear);
    if (filters.warehouse) queryParams.append("warehouse", filters.warehouse);
    if (filters.country) queryParams.append("country", filters.country);
    if (filters.product) queryParams.append("product", filters.product);
    if (filters.sapcode) queryParams.append("sapcode", filters.sapcode);
    appendSatelliteToSearchParams(queryParams, satellite);
    if (bustCache) queryParams.append("_", String(Date.now()));
    const qs = queryParams.toString();
    const endpoint = `/inventories_tmp/${qs ? `?${qs}` : ""}`;

    console.log("fetchInventoriesTmp=============>", endpoint);
    // Cache-busting is done via the `_` query param above. Do NOT send a
    // `Cache-Control` request header: it isn't in the backend's CORS
    // allow-list, so it makes the preflight fail and the browser blocks
    // the request entirely.
    return axiosService
      .get(endpoint)
      .then((res) => {
        console.log("res=============>", res);
        if (seq !== fetchSeqRef.current) return;
        const data = res.data;
        setInventories(Array.isArray(data) ? data : data?.results || []);
        if (bustCache) setTableRefreshKey((k) => k + 1);
      })
      .catch((err) => {
        if (seq !== fetchSeqRef.current) return;
        console.error("Failed to load inventories_tmp:", err);
        setInventories([]);
      })
      .finally(() => {
        if (seq === fetchSeqRef.current) setLoadingTable(false);
      });
  }, [satellite]);

  useEffect(() => {
    fetchInventoriesTmp(appliedFilters);
  }, [fetchInventoriesTmp, appliedFilters]);

  useEffect(() => {
    let cancelled = false;
    setLoadingOptions(true);

    axiosService
      .get("/years/")
      .then((yearsRes) => {
        if (cancelled) return;
        const years = (Array.isArray(yearsRes.data) ? yearsRes.data : (yearsRes.data?.results ?? []))
          .filter((item) => item.isactive === true)
          .map((item) => item.fyname);
        setFiscalYears(years);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("Failed to load fiscal years:", err);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingOptions(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const resetMessages = () => {
    setUploadResult(null);
    setUploadError(null);
  };

  const onClearForm = () => {
    setFiscalYear("");
    setMonth("july");
    setFile(null);
    setUploadProgress(null);
    resetMessages();
  };

  const handleUpload = async () => {
    resetMessages();

    if (!file) {
      setUploadError({ error: "No file provided" });
      return;
    }

    const fy = String(fiscalYear).trim();
    const mo = String(month).trim();

    if (!fy) {
      setUploadError({ error: "Missing required field: fiscal_year" });
      return;
    }
    if (!mo || !MONTH_OPTIONS.includes(mo)) {
      setUploadError({ error: "Missing/invalid required field: month" });
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("fiscal_year", fy);
    formData.append("month", mo);

    setUploading(true);
    setUploadProgress(0);
    try {
      const res = await axiosService.post("/inventories-tmp-upload/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          const pct = Math.round((evt.loaded / evt.total) * 100);
          setUploadProgress(Number.isFinite(pct) ? pct : null);
        },
      });
      setUploadResult(res.data);
      // Refresh just the table (no full-page reload). Clear filters so the
      // freshly uploaded rows are visible, mirroring the old reload behavior.
      setFilterInputs(EMPTY_FILTERS);
      setAppliedFilters(EMPTY_FILTERS);
      await fetchInventoriesTmp(EMPTY_FILTERS, { bustCache: true });
    } catch (err) {
      const data = err?.response?.data;
      setUploadError(data || { error: err?.message || "Upload failed" });
    } finally {
      setUploading(false);
    }
  };

  const handleCopyToInventories = async () => {
    setCopyResult(null);
    setCopyError(null);
    setCopying(true);
    try {
      const res = await axiosService.post("/inventories_tmp/copy_to_inventories/", {});
      setCopyResult(res.data);
      await fetchInventoriesTmp(appliedFilters, { bustCache: true });
    } catch (err) {
      const data = err?.response?.data;
      setCopyError(data || { error: err?.message || "Copy failed" });
    } finally {
      setCopying(false);
    }
  };


  const handleTruncate = async () => {
    if (!window.confirm("This will permanently clear all rows from inventories_tmp. Continue?")) {
      return;
    }
    setTruncateResult(null);
    setTruncateError(null);
    setTruncating(true);
    try {
      const res = await axiosService.post("/inventories_tmp/truncate/", {});
      setTruncateResult(res.data);
      await fetchInventoriesTmp(appliedFilters, { bustCache: true });
    } catch (err) {
      const data = err?.response?.data;
      setTruncateError(data || { error: err?.message || "Truncate failed" });
    } finally {
      setTruncating(false);
    }
  };

  return (
    <div className="scrollable-container">
      <MyPage />
      <SubTabsWarehouses />

      <div style={{ padding: "10px" }}>
        <div style={{ fontWeight: "bold", fontSize: "13px", marginBottom: "8px" }}>
           Import LEO inventory file
        </div>

        <div
          className="d-flex flex-row flex-wrap align-items-end"
          style={{ gap: "10px", padding: "8px 0" }}
        >
          <div className="d-flex flex-column" style={{ minWidth: 140 }}>
            <div style={{ fontSize: "12px", height: "16px", lineHeight: "16px" }}>Fiscal year</div>
            <select
              className="form-select"
              style={{ width: "120px", height: "30px", fontSize: "12px", marginTop: "4px" }}
              value={fiscalYear}
              onChange={(e) => setFiscalYear(e.target.value)}
              disabled={disabled || uploading || loadingOptions}
            >
              <option value="">Select fiscal year</option>
              {fiscalYears.map((fy) => (
                <option key={fy} value={fy}>
                  {fy}
                </option>
              ))}
            </select>
          </div>

          <div className="d-flex flex-column" style={{ minWidth: 160 }}>
            <div style={{ fontSize: "12px", height: "16px", lineHeight: "16px" }}>Month</div>
            <select
              className="form-select"
              style={{ width: "140px", height: "30px", fontSize: "12px", marginTop: "4px" }}
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              disabled={disabled || uploading}
            >
              {monthOptions.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>

          <div className="d-flex flex-column" style={{ minWidth: 260 }}>
            <div style={{ fontSize: "12px", height: "16px", lineHeight: "16px" }}>File (.xls/.xlsx/.csv)</div>
            <input
              className="form-control"
              type="file"
              style={{ width: "240px", height: "30px", fontSize: "12px", marginTop: "4px" }}
              accept=".xls,.xlsx,.csv"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              disabled={disabled || uploading}
            />
          </div>

          <div className="d-flex flex-row" style={{ gap: "6px" }}>
            <button
              type="button"
              className="btn btn-primary"
              style={{ height: "30px", fontSize: "12px" }}
              onClick={handleUpload}
              disabled={disabled || uploading}
            >
              {uploading ? "Uploading..." : "Upload"}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              style={{ height: "30px", fontSize: "12px" }}
              onClick={onClearForm}
              disabled={uploading}
            >
              Clear
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              style={{ height: "30px", fontSize: "12px" }}
              onClick={handleCopyToInventories}
              disabled={disabled || uploading || copying}
            >
              {copying ? "Copying..." : "Copy to Inventories"}
            </button>
            <button
              type="button"
              className="btn btn-danger"
              style={{ height: "30px", fontSize: "12px" }}
              onClick={handleTruncate}
              disabled={disabled || uploading || copying || truncating}
            >
              {truncating ? "Truncating..." : "Truncate"}
            </button>
          </div>
        </div>

        {uploading && (
          <div style={{ marginTop: "10px", fontSize: "12px" }}>
            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
            Uploading{uploadProgress != null ? `… ${uploadProgress}%` : "…"}
          </div>
        )}

        {uploadResult && (
          <div className="alert alert-success" style={{ marginTop: "10px", fontSize: "12px" }}>
            <div style={{ fontWeight: "bold" }}>{uploadResult.success || "Upload completed"}</div>
            <div>
              fiscal_year: <b>{uploadResult.fiscal_year}</b> — warehouses:{" "}
              <b>{Array.isArray(uploadResult.warehouses) ? uploadResult.warehouses.join(", ") : uploadResult.warehouses}</b> — month:{" "}
              <b>{uploadResult.month}</b>
            </div>
            <div>
              csv_rows: <b>{uploadResult.csv_rows}</b> — unique_part_numbers: <b>{uploadResult.unique_part_numbers}</b> — created:{" "}
              <b>{uploadResult.created}</b> — updated: <b>{uploadResult.updated}</b>
            </div>
            {Array.isArray(uploadResult.errors) && uploadResult.errors.length > 0 && (
              <div style={{ marginTop: "8px" }}>
                <div style={{ fontWeight: "bold" }}>
                  Errors ({uploadResult.errors.length})
                </div>
                <ul style={{ marginBottom: 0 }}>
                  {uploadResult.errors.slice(0, 50).map((e, idx) => (
                    <li key={`${e.part_number || "row"}-${idx}`}>
                      <b>{e.part_number || "?"}</b>: {e.error || "Unknown error"}
                    </li>
                  ))}
                </ul>
                {uploadResult.errors.length > 50 && (
                  <div>Showing first 50 of {uploadResult.errors.length} errors.</div>
                )}
              </div>
            )}
          </div>
        )}

        {uploadError && (
          <div className="alert alert-danger" style={{ marginTop: "10px", fontSize: "12px" }}>
            {uploadError.error && <div style={{ fontWeight: "bold" }}>{uploadError.error}</div>}
            {Array.isArray(uploadError.errors) && uploadError.errors.length > 0 && (
              <div style={{ marginTop: "6px" }}>
                <div style={{ fontWeight: "bold" }}>Errors</div>
                <ul style={{ marginBottom: 0 }}>
                  {uploadError.errors.slice(0, 50).map((e, idx) => (
                    <li key={`${e.part_number || "row"}-${idx}`}>
                      <b>{e.part_number || "?"}</b>: {e.error || "Unknown error"}
                    </li>
                  ))}
                </ul>
                {uploadError.errors.length > 50 && <div>Showing first 50 errors.</div>}
              </div>
            )}
          </div>
        )}

        {copyResult && (
          <div className="alert alert-success" style={{ marginTop: "10px", fontSize: "12px" }}>
            <div style={{ fontWeight: "bold" }}>{copyResult.message || "Copy completed"}</div>
            {copyResult.rows_processed != null && (
              <div>
                rows_processed: <b>{copyResult.rows_processed}</b> — created: <b>{copyResult.created}</b> — updated:{" "}
                <b>{copyResult.updated}</b>
              </div>
            )}
          </div>
        )}

        {copyError && (
          <div className="alert alert-danger" style={{ marginTop: "10px", fontSize: "12px" }}>
            {copyError.error || "Copy failed"}
          </div>
        )}

        {truncateResult && (
          <div className="alert alert-success" style={{ marginTop: "10px", fontSize: "12px" }}>
            <div style={{ fontWeight: "bold" }}>{truncateResult.message || "Truncate completed"}</div>
            {truncateResult.rows_removed != null && (
              <div>
                rows_removed: <b>{truncateResult.rows_removed}</b>
              </div>
            )}
          </div>
        )}

        {truncateError && (
          <div className="alert alert-danger" style={{ marginTop: "10px", fontSize: "12px" }}>
            {truncateError.error || "Truncate failed"}
          </div>
        )}

        <div style={{ marginTop: "8px", fontSize: "12px", color: "#666" }}>
          Upload stock files into <b>inventories_tmp</b>. The warehouse is derived from the{" "}
          <b>Stock Room ID</b> column in the file (OneWeb FL → Florida, OneWeb EUR → Amsterdam, OneWeb
          AUS → Australia, OneWebKOR2 → South Korea); quantities come from the <b>Available</b> column.
          The current inventory is shown below.
        </div>
      </div>

      <hr style={{ border: "none", borderTop: "1px solid #e0e0e0", margin: "16px 0" }} />

      <div className="table-container" style={{ height: '700px', padding: "10px" }}>
      <InventoriesTable
        key={tableRefreshKey}
        inventories={inventories}
        height="750px"
        loading={loadingTable}
        showActions={false}
        showFilters={true}
        fiscalYearValue={filterInputs.fiscalYear}
        onFiscalYearChange={setFilter("fiscalYear")}
        onWarehouseNameChange={setFilter("warehouse")}
        onCountryNameChange={setFilter("country")}
        onProductIdChange={setFilter("sapcode")}
        onProductDescriptionChange={setFilter("product")}
        onSearch={handleSearchInventories}
        onClear={handleClearFilters}
      />
      </div>
    </div>
  );
}